import { debug } from '@/utils/logger';

/**
 * Whether the signed-in viewer follows each author, shared app-wide so every Follow button agrees
 * (profile header, the "Trip by" bar on cards, search). Public profile/post payloads are fetched
 * without auth and never carry a real `is_following`, so the truth comes from the viewer's
 * following list (loaded once per session) plus every follow/unfollow made since.
 */
type FollowChangeListener = (authorId: string, isFollowing: boolean) => void;
/** Returns the users `viewerId` follows (passed in to keep this module free of API imports). */
type FollowingLoader = (viewerId: string) => Promise<Array<{ _id?: string | number }>>;

const followingByAuthorId = new Map<string, boolean>();
const storeListeners = new Set<() => void>();
const changeListeners = new Set<FollowChangeListener>();

let loadedViewerId: string | null = null;
let loadingViewerId: string | null = null;
let loadPromise: Promise<void> | null = null;

function notifyStore() {
  storeListeners.forEach((listener) => listener());
}

export function subscribeAuthorFollowStore(listener: () => void) {
  storeListeners.add(listener);
  return () => {
    storeListeners.delete(listener);
  };
}

/** Records a follow/unfollow (optimistic or confirmed) and tells `onAuthorFollowingChange` listeners. */
export function setAuthorFollowing(authorId: string, isFollowing: boolean) {
  if (!authorId || followingByAuthorId.get(authorId) === isFollowing) return;
  followingByAuthorId.set(authorId, isFollowing);
  notifyStore();
  changeListeners.forEach((listener) => listener(authorId, isFollowing));
}

/** Calls `listener` whenever an author's follow state changes (not on the initial load); returns the unsubscribe. */
export function onAuthorFollowingChange(listener: FollowChangeListener) {
  changeListeners.add(listener);
  return () => {
    changeListeners.delete(listener);
  };
}

/** True/false once known — after the viewer's following list loaded, or a follow/unfollow this session. */
export function getAuthorFollowing(authorId: string | undefined): boolean | undefined {
  if (!authorId) return undefined;
  const known = followingByAuthorId.get(authorId);
  if (known !== undefined) return known;
  return loadedViewerId ? false : undefined;
}

/** Loads the viewer's following list once per session (deduped); resolves even if the request fails. */
export function ensureViewerFollowingLoaded(viewerId: string, loadFollowing: FollowingLoader): Promise<void> {
  if (!viewerId) return Promise.resolve();
  if (loadedViewerId === viewerId) return Promise.resolve();
  if (loadingViewerId === viewerId && loadPromise) return loadPromise;

  // A different viewer signed in: drop the previous viewer's state.
  if (loadedViewerId && loadedViewerId !== viewerId) {
    followingByAuthorId.clear();
    loadedViewerId = null;
  }

  loadingViewerId = viewerId;
  loadPromise = loadFollowing(viewerId)
    .then((users) => {
      if (loadingViewerId !== viewerId) return;
      for (const followed of users) {
        const followedId = followed?._id ? String(followed._id) : '';
        // A follow/unfollow made while this was loading is newer than the list.
        if (followedId && !followingByAuthorId.has(followedId)) {
          followingByAuthorId.set(followedId, true);
        }
      }
      loadedViewerId = viewerId;
      notifyStore();
    })
    .catch((err) => {
      debug('[authorFollowStore] Could not load the viewer following list', err);
    })
    .finally(() => {
      if (loadingViewerId === viewerId) {
        loadingViewerId = null;
        loadPromise = null;
      }
    });
  return loadPromise;
}
